import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, ShieldCheck, KeyRound, Mail, History, LogOut, Eye, EyeOff,
  Check, X, Loader2, MapPin, Monitor, Smartphone, Tablet, Clock,
  Lock, Bell, KeySquare, Users, ScrollText, Fingerprint,
} from "lucide-react";
import { api, clearToken, getToken } from "@/lib/auth";
import { BrandLogo } from "@/components/BrandLogo";

const RULES = [
  { key: "len", label: "At least 12 characters", test: (v) => v.length >= 12 },
  { key: "upper", label: "One uppercase letter", test: (v) => /[A-Z]/.test(v) },
  { key: "lower", label: "One lowercase letter", test: (v) => /[a-z]/.test(v) },
  { key: "num", label: "One number", test: (v) => /[0-9]/.test(v) },
  { key: "special", label: "One special character", test: (v) => /[^A-Za-z0-9]/.test(v) },
];

const NAV = [
  { key: "security", label: "Security", icon: ShieldCheck, available: true },
  { key: "activity", label: "Login Activity", icon: History, available: true },
  { key: "twofa", label: "Two-Factor Auth", icon: Fingerprint, available: false },
  { key: "apikeys", label: "API Keys", icon: KeySquare, available: false },
  { key: "notifications", label: "Notifications", icon: Bell, available: false },
  { key: "users", label: "Users & Roles", icon: Users, available: false },
  { key: "audit", label: "Security Audit Log", icon: ScrollText, available: false },
];

const cardCls = "bg-[#0a0a0a] border border-white/10 rounded-sm";
const labelCls = "mono text-[9px] uppercase tracking-[0.25em] text-[#737373] mb-2 block";
const btnGold =
  "mono text-[10px] uppercase tracking-[0.2em] px-6 py-4 bg-[#f4f0ea] text-[#030303] rounded-full hover:bg-[#d4af37] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2";

function PasswordField({ value, onChange, placeholder, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        data-testid={testid}
        type={show ? "text" : "password"}
        className="field pr-12"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        data-testid={`${testid}-toggle`}
        onClick={() => setShow((s) => !s)}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#d4af37] transition-colors"
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">{children}</span>
      <span className="h-px flex-1 bg-white/10" />
    </div>
  );
}

function DeviceIcon({ device, size = 14 }) {
  if (device === "Mobile") return <Smartphone size={size} />;
  if (device === "Tablet") return <Tablet size={size} />;
  return <Monitor size={size} />;
}

export default function AccountSettings() {
  const nav = useNavigate();
  const [tab, setTab] = useState("security");
  const [me, setMe] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // change password state
  const [cur, setCur] = useState("");
  const [np, setNp] = useState("");
  const [cnp, setCnp] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  // change email state
  const [emailPw, setEmailPw] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);

  const [logoutSaving, setLogoutSaving] = useState(false);

  const forceRelogin = useCallback((msg) => {
    toast.success(msg);
    clearToken();
    setTimeout(() => nav("/admin/login"), 900);
  }, [nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, h] = await Promise.all([api.get("/auth/me"), api.get("/auth/login-history")]);
      setMe(m.data);
      setHistory(h.data);
    } catch (err) {
      if (err?.response?.status === 401) { clearToken(); nav("/admin/login"); return; }
      toast.error("Failed to load account settings");
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    if (!getToken()) { nav("/admin/login"); return; }
    load();
  }, [load, nav]);

  const rulesPass = RULES.every((r) => r.test(np));
  const matchPass = np.length > 0 && np === cnp;
  const canSavePw = cur.length > 0 && rulesPass && matchPass && !pwSaving;

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!canSavePw) return;
    setPwSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: cur, new_password: np });
      forceRelogin(data.message || "Password updated. Please sign in again.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
      setPwSaving(false);
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setEmailSaving(true);
    try {
      const { data } = await api.post("/auth/change-email", { current_password: emailPw, new_email: newEmail });
      forceRelogin(data.message || "Login email updated. Please sign in again.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change email");
      setEmailSaving(false);
    }
  };

  const logoutAll = async () => {
    setLogoutSaving(true);
    try {
      const { data } = await api.post("/auth/logout-all", {});
      forceRelogin(data.message || "All sessions signed out.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign out sessions");
      setLogoutSaving(false);
    }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div className="min-h-screen grain bg-[#030303] text-[#f4f0ea]">
      <header className="sticky top-0 z-40 bg-[#030303]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BrandLogo size="sm" />
            <span className="hidden sm:inline-block h-6 w-px bg-white/10" />
            <span className="hidden sm:inline mono text-[9px] uppercase tracking-[0.3em] text-[#d4af37]">JCI Admin Portal</span>
          </div>
          <button
            data-testid="back-to-dashboard"
            onClick={() => nav("/admin")}
            className="mono text-[10px] uppercase tracking-[0.2em] px-5 py-3 border border-white/15 rounded-full hover:border-[#d4af37]/50 hover:text-[#d4af37] flex items-center gap-2"
          >
            <ArrowLeft size={13} /> Dashboard
          </button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12">
        <div className="mb-10">
          <div className="mono text-[10px] uppercase tracking-[0.35em] text-[#7fb0ff] mb-3">Account Settings</div>
          <h1 className="serif text-4xl lg:text-5xl text-[#f4f0ea]">Security & Access</h1>
          {me?.email && (
            <p className="text-[#a3a3a3] text-sm mt-3">Signed in as <span className="text-[#f4f0ea]">{me.email}</span></p>
          )}
        </div>

        {loading ? (
          <div className="py-24 text-center mono text-xs uppercase tracking-[0.2em] text-[#737373]">Loading…</div>
        ) : (
          <div className="grid lg:grid-cols-[240px_1fr] gap-8 lg:gap-12">
            {/* Sidebar nav */}
            <nav data-testid="settings-nav" className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
              {NAV.map((item) => {
                const Icon = item.icon;
                const activeItem = tab === item.key;
                let stateCls;
                if (activeItem) {
                  stateCls = "bg-[#d4af37]/10 text-[#d4af37] border-l-2 border-[#d4af37]";
                } else if (item.available) {
                  stateCls = "text-[#a3a3a3] hover:text-[#f4f0ea] hover:bg-white/[0.03] border-l-2 border-transparent";
                } else {
                  stateCls = "text-[#4a4a4a] cursor-not-allowed border-l-2 border-transparent";
                }
                return (
                  <button
                    key={item.key}
                    data-testid={`settings-tab-${item.key}`}
                    disabled={!item.available}
                    onClick={() => item.available && setTab(item.key)}
                    className={`shrink-0 text-left flex items-center gap-3 px-4 py-3 rounded-sm mono text-[10px] uppercase tracking-[0.2em] transition-colors ${stateCls}`}
                  >
                    <Icon size={14} />
                    <span className="whitespace-nowrap">{item.label}</span>
                    {!item.available && <span className="ml-auto text-[8px] tracking-[0.15em] text-[#4a4a4a] hidden lg:inline">Soon</span>}
                  </button>
                );
              })}
            </nav>

            {/* Content */}
            <div className="min-w-0">
              {tab === "security" && (
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="space-y-12">
                  {/* Change Password */}
                  <section data-testid="change-password-section">
                    <SectionTitle>Change Password</SectionTitle>
                    <div className={`${cardCls} p-6 lg:p-8`}>
                      <form onSubmit={submitPassword} className="space-y-6 max-w-lg">
                        <div>
                          <label className={labelCls}>Current password</label>
                          <PasswordField testid="current-password" value={cur} onChange={setCur} placeholder="Enter current password" />
                        </div>
                        <div>
                          <label className={labelCls}>New password</label>
                          <PasswordField testid="new-password" value={np} onChange={setNp} placeholder="Enter new password" />
                        </div>
                        <div>
                          <label className={labelCls}>Confirm new password</label>
                          <PasswordField testid="confirm-password" value={cnp} onChange={setCnp} placeholder="Re-enter new password" />
                          {cnp.length > 0 && !matchPass && (
                            <p className="text-red-400 text-xs mt-2 flex items-center gap-1"><X size={12} /> Passwords do not match</p>
                          )}
                        </div>

                        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 pt-1" data-testid="password-rules">
                          {RULES.map((r) => {
                            const ok = r.test(np);
                            return (
                              <li key={r.key} className={`flex items-center gap-2 text-xs ${ok ? "text-[#7fb0ff]" : "text-[#737373]"}`}>
                                {ok ? <Check size={13} /> : <span className="w-[13px] h-[13px] rounded-full border border-current inline-block" />}
                                {r.label}
                              </li>
                            );
                          })}
                        </ul>

                        <button type="submit" data-testid="save-password" disabled={!canSavePw} className={btnGold}>
                          {pwSaving ? <><Loader2 className="animate-spin" size={14} /> Saving…</> : <><KeyRound size={14} /> Update Password</>}
                        </button>
                        <p className="text-[#737373] text-xs">For your security, you'll be signed out and asked to log in again after changing your password.</p>
                      </form>
                    </div>
                  </section>

                  {/* Change Email */}
                  <section data-testid="change-email-section">
                    <SectionTitle>Change Login Email</SectionTitle>
                    <div className={`${cardCls} p-6 lg:p-8`}>
                      <form onSubmit={submitEmail} className="space-y-6 max-w-lg">
                        <div>
                          <label className={labelCls}>New login email</label>
                          <input data-testid="new-email" type="email" required className="field" placeholder="name@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
                        </div>
                        <div>
                          <label className={labelCls}>Confirm with current password</label>
                          <PasswordField testid="email-current-password" value={emailPw} onChange={setEmailPw} placeholder="Enter current password" />
                        </div>
                        <button type="submit" data-testid="save-email" disabled={emailSaving || !newEmail || !emailPw} className={btnGold}>
                          {emailSaving ? <><Loader2 className="animate-spin" size={14} /> Saving…</> : <><Mail size={14} /> Update Email</>}
                        </button>
                        <p className="text-[#737373] text-xs">You'll be signed out and asked to log in with your new email.</p>
                      </form>
                    </div>
                  </section>

                  {/* Logout all */}
                  <section data-testid="logout-all-section">
                    <SectionTitle>Sessions</SectionTitle>
                    <div className={`${cardCls} p-6 lg:p-8 flex flex-col sm:flex-row sm:items-center gap-5 justify-between`}>
                      <div className="max-w-md">
                        <div className="text-[#f4f0ea] flex items-center gap-2 mb-1"><Lock size={15} className="text-[#d4af37]" /> Log out of all devices</div>
                        <p className="text-[#a3a3a3] text-sm">Immediately sign out every active session, including this one. Use this if you suspect unauthorized access.</p>
                      </div>
                      <button
                        data-testid="logout-all-btn"
                        onClick={logoutAll}
                        disabled={logoutSaving}
                        className="shrink-0 mono text-[10px] uppercase tracking-[0.2em] px-6 py-4 border border-red-500/40 text-red-400 rounded-full hover:bg-red-500/10 transition-colors flex items-center gap-2 disabled:opacity-50"
                      >
                        {logoutSaving ? <Loader2 className="animate-spin" size={14} /> : <LogOut size={14} />} Sign out everywhere
                      </button>
                    </div>
                  </section>
                </motion.div>
              )}

              {tab === "activity" && (
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="space-y-12">
                  {/* Last login */}
                  <section data-testid="last-login-section">
                    <SectionTitle>Last Login</SectionTitle>
                    <div className={`${cardCls} p-6 lg:p-8`}>
                      {me?.last_login ? (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                          {[
                            ["When", fmt(me.last_login.created_at), Clock],
                            ["Location", me.last_login.location || "Unknown", MapPin],
                            ["Browser", `${me.last_login.browser} · ${me.last_login.os}`, Monitor],
                            ["IP Address", me.last_login.ip || "—", ShieldCheck],
                          ].map(([label, val, Icon]) => (
                            <div key={label}>
                              <div className="mono text-[9px] uppercase tracking-[0.25em] text-[#737373] mb-2 flex items-center gap-2"><Icon size={12} className="text-[#d4af37]" /> {label}</div>
                              <div className="text-[#f4f0ea]/90 text-sm break-words">{val}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[#737373] text-sm">No login activity recorded yet.</p>
                      )}
                    </div>
                  </section>

                  {/* History */}
                  <section data-testid="login-history-section">
                    <SectionTitle>Recent Logins</SectionTitle>
                    <div className={`${cardCls} overflow-hidden`}>
                      {history.length === 0 ? (
                        <div className="p-8 text-[#737373] text-sm">No login history yet.</div>
                      ) : (
                        history.map((h) => (
                          <div key={h.id} data-testid={`login-event-${h.id}`} className="grid grid-cols-[auto_1fr_auto] sm:grid-cols-[auto_1.2fr_1fr_1fr] gap-4 items-center px-6 py-5 border-b border-white/10 last:border-0">
                            <span className="text-[#d4af37]"><DeviceIcon device={h.device} size={16} /></span>
                            <div className="min-w-0">
                              <div className="text-[#f4f0ea] text-sm truncate">{h.browser} · {h.os}</div>
                              <div className="text-[#737373] text-xs mono">{h.device}</div>
                            </div>
                            <div className="text-[#a3a3a3] text-sm hidden sm:flex items-center gap-2 min-w-0">
                              <MapPin size={12} className="text-[#737373] shrink-0" />
                              <span className="truncate">{h.location || "Unknown"}</span>
                            </div>
                            <div className="text-right">
                              <div className="text-[#a3a3a3] text-xs mono">{fmt(h.created_at)}</div>
                              <div className="text-[#737373] text-[11px] mono">{h.ip || "—"}</div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </section>
                </motion.div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
